import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, FormFeedback, Input, Label, Row } from "reactstrap";
import AppModal from "../../../../../Components/Common/AppModal";
import Select from "react-select";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import {
  createEmailInboundRule,
  createEmailInboundRuleSuccess,
  updateEmailInboundRule,
  updateEmailInboundRuleSuccess,
} from "../../../../../store/CRMitEmailInbox/action";
import { MATCH_ALL, MATCH_ANY, RULE_ACTIONS, emptyConditionRow } from "./constants";

const blankRule = {
  Title: "",
  Description: "",
  FlagActive: 1,
  FlagMatchAll: MATCH_ALL,
  SortOrder: 10,
  EmailAccount_Id: null,
  FlagRunOnRuleCreation: 0,
  emailinboundruleitem_list: [emptyConditionRow()],
};

export default function CreateEditEmailRule({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
  fieldOptions,
  operandOptions,
  onSaved,
  // Optional create-mode seed: { Title, FlagMatchAll, conditions: [{ EmailInboundRuleItemField_Id, SystemFunctionOperand_Id, Value }] }
  prefill,
}) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(blankRule);
  const [submitted, setSubmitted] = useState(false);

  const { createLoading, updateLoading, createAPI, updateAPI } = useSelector(
    (state) => ({
      createLoading: state.EmailInbound.emailInboundRuleCreateLoading,
      updateLoading: state.EmailInbound.emailInboundRuleUpdateLoading,
      createAPI: state.EmailInbound.emailInboundRuleCreate,
      updateAPI: state.EmailInbound.emailInboundRuleUpdate,
    })
  );
  const loading = createLoading || updateLoading;

  // Hydrate the form when editing an existing rule.
  useEffect(() => {
    if (isEdit && !isEmpty(updateData)) {
      const actionFlags = {};
      RULE_ACTIONS.forEach((a) => {
        actionFlags[a.key] = Number(updateData[a.key]) === 1 ? 1 : 0;
      });
      setForm({
        Id: updateData.Id,
        Title: updateData.Title || "",
        Description: updateData.Description || "",
        FlagActive: Number(updateData.FlagActive) === 1 ? 1 : 0,
        FlagMatchAll: Number(updateData.FlagMatchAll) === 1 ? MATCH_ALL : MATCH_ANY,
        SortOrder: Number(updateData.SortOrder) || 10,
        EmailAccount_Id: updateData.EmailAccount_Id || null,
        FlagRunOnRuleCreation: Number(updateData.FlagRunOnRuleCreation) === 1 ? 1 : 0,
        ...actionFlags,
        emailinboundruleitem_list: (updateData.emailinboundruleitem_list || []).map(
          (it) => ({
            Id: it.Id,
            EmailInboundRuleItemField_Id: Number(it.EmailInboundRuleItemField_Id) || "",
            SystemFunctionOperand_Id: Number(it.SystemFunctionOperand_Id) || "",
            Value: it.Value || "",
          })
        ),
      });
    } else if (!isEmpty(prefill)) {
      // Create mode, pre-seeded (e.g. opened from a mailbox email).
      const seededConditions =
        Array.isArray(prefill.conditions) && prefill.conditions.length
          ? prefill.conditions.map((c) => ({
              EmailInboundRuleItemField_Id:
                c.EmailInboundRuleItemField_Id || "",
              SystemFunctionOperand_Id: c.SystemFunctionOperand_Id || "",
              Value: c.Value || "",
            }))
          : [emptyConditionRow()];
      setForm({
        ...blankRule,
        Title: prefill.Title || "",
        FlagMatchAll:
          prefill.FlagMatchAll != null ? prefill.FlagMatchAll : MATCH_ALL,
        emailinboundruleitem_list: seededConditions,
      });
    } else {
      setForm({ ...blankRule, emailinboundruleitem_list: [emptyConditionRow()] });
    }
    setSubmitted(false);
  }, [isEdit, updateData, modal, prefill]);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const setCondition = (index, key, value) =>
    setForm((p) => ({
      ...p,
      emailinboundruleitem_list: p.emailinboundruleitem_list.map((row, i) =>
        i === index ? { ...row, [key]: value } : row
      ),
    }));

  const addCondition = () =>
    setForm((p) => ({
      ...p,
      emailinboundruleitem_list: [...p.emailinboundruleitem_list, emptyConditionRow()],
    }));

  const removeCondition = (index) =>
    setForm((p) => ({
      ...p,
      emailinboundruleitem_list: p.emailinboundruleitem_list.filter(
        (_, i) => i !== index
      ),
    }));

  const selectedActionKeys = RULE_ACTIONS.filter((a) => form[a.key] === 1).map(
    (a) => a.key
  );

  const validConditions = form.emailinboundruleitem_list.filter(
    (c) =>
      c.EmailInboundRuleItemField_Id &&
      c.SystemFunctionOperand_Id &&
      String(c.Value).trim()
  );

  const titleInvalid = submitted && !form.Title.trim();
  const conditionsInvalid = submitted && validConditions.length === 0;
  const actionInvalid = submitted && selectedActionKeys.length === 0;

  const handleSave = () => {
    setSubmitted(true);
    if (!form.Title.trim()) {
      toast.error("Please enter a rule title");
      return;
    }
    if (validConditions.length === 0) {
      toast.error("Add at least one complete condition");
      return;
    }
    if (selectedActionKeys.length === 0) {
      toast.error("Select at least one action");
      return;
    }

    const actionFlags = {};
    RULE_ACTIONS.forEach((a) => {
      actionFlags[a.key] = form[a.key] === 1 ? 1 : 0;
    });
    // Legacy single `Action` id = first enabled action.
    const firstAction = RULE_ACTIONS.find((a) => form[a.key] === 1);

    const items = validConditions.map((c) => ({
      ...(c.Id ? { Id: Number(c.Id) } : {}),
      EmailInboundRuleItemField_Id: Number(c.EmailInboundRuleItemField_Id),
      SystemFunctionOperand_Id: Number(c.SystemFunctionOperand_Id),
      Value: String(c.Value).trim(),
    }));

    const base = {
      Title: form.Title.trim(),
      Description: form.Description?.trim() || "",
      FlagActive: form.FlagActive ? 1 : 0,
      FlagMatchAll: form.FlagMatchAll ? 1 : 0,
      EmailAccount_Id: form.EmailAccount_Id || null,
      Action: firstAction?.actionId ?? 1,
      FlagRunOnRuleCreation: form.FlagRunOnRuleCreation ? 1 : 0,
      ...actionFlags,
      emailinboundruleitem_list: items,
    };

    if (isEdit) {
      dispatch(
        updateEmailInboundRule([
          {
            Id: Number(form.Id),
            ...base,
            // API update spec uses `Order`; send both to be safe.
            Order: Number(form.SortOrder) || 10,
            SortOrder: Number(form.SortOrder) || 10,
          },
        ])
      );
    } else {
      dispatch(
        createEmailInboundRule([
          {
            ...base,
            SortOrder: Number(form.SortOrder) || 10,
          },
        ])
      );
    }
  };

  // Create response
  useEffect(() => {
    if (isEmpty(createAPI)) return;
    if (createAPI?.status === 200) {
      const archived = Number(createAPI?.QtyEmailsArchived || 0);
      toast.success(
        archived > 0
          ? `${createAPI.message} — ${archived} existing email(s) archived`
          : createAPI.message
      );
      onSaved?.();
      setModal(false);
    } else {
      toast.error(createAPI?.message || "Failed to create rule");
    }
    dispatch(createEmailInboundRuleSuccess([]));
  }, [createAPI]);

  // Update response
  useEffect(() => {
    if (isEmpty(updateAPI)) return;
    if (updateAPI?.status === 200) {
      toast.success(updateAPI.message);
      onSaved?.();
      setModal(false);
    } else {
      toast.error(updateAPI?.message || "Failed to update rule");
    }
    dispatch(updateEmailInboundRuleSuccess([]));
  }, [updateAPI]);

  const operandHint = useMemo(() => {
    const map = {};
    (operandOptions || []).forEach((o) => (map[o.value] = o.description));
    return map;
  }, [operandOptions]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={isEdit ? "Edit Email Rule" : "Create Email Rule"}
      headerAccent="indigo"
      size="md"
      loading={loading}
      onSubmit={handleSave}
      submitLabel={isEdit ? "Update Rule" : "Create Rule"}
      submitColor="primary"
    >
        <Row className="g-3">
          <Col md={8}>
            <Label className="form-label">
              Title <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              placeholder="e.g. Archive newsletter emails"
              value={form.Title}
              invalid={titleInvalid}
              onChange={(e) => setField("Title", e.target.value)}
            />
            <FormFeedback>Title is required</FormFeedback>
          </Col>
          <Col md={4}>
            <Label className="form-label">Priority (sort order)</Label>
            <Input
              type="number"
              value={form.SortOrder}
              onChange={(e) => setField("SortOrder", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Label className="form-label">Description</Label>
            <Input
              type="textarea"
              rows={2}
              placeholder="What does this rule do?"
              value={form.Description}
              onChange={(e) => setField("Description", e.target.value)}
            />
          </Col>
        </Row>

        <hr />

        {/* Conditions */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0">Conditions</h6>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn btn-${form.FlagMatchAll ? "primary" : "outline-primary"}`}
              onClick={() => setField("FlagMatchAll", MATCH_ALL)}
            >
              Match ALL
            </button>
            <button
              type="button"
              className={`btn btn-${!form.FlagMatchAll ? "primary" : "outline-primary"}`}
              onClick={() => setField("FlagMatchAll", MATCH_ANY)}
            >
              Match ANY
            </button>
          </div>
        </div>
        <p className="text-muted fs-12 mb-2">
          {form.FlagMatchAll
            ? "An email must match every condition below."
            : "An email matching any one condition below will trigger this rule."}
        </p>

        {form.emailinboundruleitem_list.map((cond, index) => (
          <Row className="g-2 align-items-start mb-2" key={index}>
            <Col md={3}>
              <Select
                placeholder="Field…"
                classNamePrefix="select"
                value={
                  fieldOptions?.find(
                    (o) => o.value === cond.EmailInboundRuleItemField_Id
                  ) || null
                }
                options={fieldOptions}
                onChange={(opt) =>
                  setCondition(index, "EmailInboundRuleItemField_Id", opt?.value || "")
                }
              />
            </Col>
            <Col md={3}>
              <Select
                placeholder="Operator…"
                classNamePrefix="select"
                value={
                  operandOptions?.find(
                    (o) => o.value === cond.SystemFunctionOperand_Id
                  ) || null
                }
                options={operandOptions}
                onChange={(opt) =>
                  setCondition(index, "SystemFunctionOperand_Id", opt?.value || "")
                }
              />
              {cond.SystemFunctionOperand_Id &&
                operandHint[cond.SystemFunctionOperand_Id] && (
                  <small className="text-muted">
                    {operandHint[cond.SystemFunctionOperand_Id]}
                  </small>
                )}
            </Col>
            <Col md={5}>
              <Input
                type="text"
                placeholder="Value e.g. newsletter, @spam.com"
                value={cond.Value}
                onChange={(e) => setCondition(index, "Value", e.target.value)}
              />
            </Col>
            <Col md={1} className="text-end">
              <button
                type="button"
                className="btn btn-soft-danger btn-icon"
                title="Remove condition"
                disabled={form.emailinboundruleitem_list.length === 1}
                onClick={() => removeCondition(index)}
              >
                <i className="ri-close-line" />
              </button>
            </Col>
          </Row>
        ))}
        {conditionsInvalid && (
          <div className="text-danger fs-12 mb-2">
            Add at least one complete condition (field, operator and value).
          </div>
        )}
        <button type="button" className="btn btn-soft-primary btn-sm" onClick={addCondition}>
          <i className="ri-add-line align-bottom me-1" />
          Add condition
        </button>

        <hr />

        {/* Actions */}
        <h6 className="mb-2">Then…</h6>
        {actionInvalid && (
          <div className="text-danger fs-12 mb-2">Select at least one action.</div>
        )}
        <Row className="g-2">
          {RULE_ACTIONS.map((action) => (
            <Col md={6} key={action.key}>
              <div
                className={`form-check card-radio p-2 border rounded ${
                  form[action.key] ? "border-primary" : ""
                }`}
              >
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id={`action-${action.key}`}
                  checked={form[action.key] === 1}
                  onChange={(e) =>
                    setField(action.key, e.target.checked ? 1 : 0)
                  }
                />
                <Label className="form-check-label ms-1" for={`action-${action.key}`}>
                  <i className={`${action.icon} me-1`} />
                  {action.label}
                  <div className="text-muted fs-12">{action.description}</div>
                </Label>
              </div>
            </Col>
          ))}
        </Row>

        <hr />

        {/* Flags */}
        <div className="d-flex align-items-center gap-4 flex-wrap">
          <div className="form-check form-switch">
            <Input
              type="checkbox"
              role="switch"
              className="form-check-input"
              id="flag-active"
              checked={form.FlagActive === 1}
              onChange={(e) => setField("FlagActive", e.target.checked ? 1 : 0)}
            />
            <Label className="form-check-label" for="flag-active">
              Rule active
            </Label>
          </div>

          <div className="form-check form-switch">
            <Input
              type="checkbox"
              role="switch"
              className="form-check-input"
              id="flag-run-on-create"
              checked={form.FlagRunOnRuleCreation === 1}
              onChange={(e) =>
                setField("FlagRunOnRuleCreation", e.target.checked ? 1 : 0)
              }
            />
            <Label className="form-check-label" for="flag-run-on-create">
              Run this rule on all mailboxes after creation
            </Label>
          </div>
        </div>
    </AppModal>
  );
}
